import React, { useCallback, useEffect, useState } from 'react';
import { History, Undo2 } from 'lucide-react';
import { apiCall } from '../_shared';
import { Card } from './StatsWidgets';

const ORIGIN = { suggestion: 'Price insight', editor: 'Rate editor', copy: 'Copy rates', undo: 'Rollback' };

const summarize = (entries) => (entries || []).slice(0, 3).map((e) => `${e.from}${e.to !== e.from ? `→${e.to}` : ''}: ${Object.entries(e).filter(([k]) => !['from', 'to'].includes(k)).map(([k, v]) => `${k.replace('price', 'P').replace('numAvail', 'avail').replace('minStay', 'min')} ${v}`).join(', ')}`).join(' · ') + ((entries || []).length > 3 ? ` · +${entries.length - 3} more` : '');

const Row = ({ p, onUndo, busy }) => {
  const canUndo = p.ok && !p.undone_at && p.origin !== 'undo';
  return (
    <li className="border-t py-2 text-xs flex items-start gap-2" data-testid={`rate-history-${p.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-slate-900"><b>{p.room}</b> · <span className="text-slate-500">{String(p.at).slice(0, 16).replace('T', ' ')} UTC · {p.by} · {ORIGIN[p.origin] || p.origin} · {p.nights} n</span>{!p.ok && <span className="ml-1 text-rose-700 font-semibold">failed</span>}{p.undone_at && <span className="ml-1 text-amber-700 font-semibold">rolled back</span>}</p>
        <p className="text-slate-600 truncate" title={summarize(p.entries)}>{summarize(p.entries)}{p.note ? <span className="text-slate-400"> — {p.note}</span> : null}</p>
      </div>
      {canUndo && <button type="button" disabled={busy === p.id} onClick={() => onUndo(p)} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold border border-amber-300 bg-amber-50 text-amber-900 rounded px-2 py-1 hover:bg-amber-100 disabled:opacity-50" data-testid={`rate-undo-${p.id}`}><Undo2 className="w-3 h-3" /> Undo</button>}
    </li>
  );
};

export const RateHistoryCard = ({ refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => apiCall('/api/admin/rates/history').then((d) => setRows(d.pushes || [])).catch((e) => setMsg(e.message)), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const undo = async (p) => {
    if (!window.confirm(`Restore the previous prices for ${p.room} (${p.nights} nights)? This pushes the saved "before" values back to Beds24.`)) return;
    setBusy(p.id); setMsg('');
    try {
      const d = await apiCall(`/api/admin/rates/undo/${p.id}`, { method: 'POST' });
      setMsg(`Rolled back ${d.nights} nights.${d.skipped?.length ? ` Not restorable: ${d.skipped.join('; ')}` : ''}`);
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(null); }
  };
  const list = rows.map((p) => <Row key={p.id} p={p} onUndo={undo} busy={busy} />);
  return (
    <Card title="Rate push history" sub="Every change sent to Beds24 with its previous values — Undo restores them with one tap" testId="rate-history">
      {msg && <p className="text-xs text-slate-700 mb-2" data-testid="rate-history-msg">{msg}</p>}
      {rows.length ? <ul>{list}</ul> : <p className="text-xs text-slate-400 flex items-center gap-1"><History className="w-3.5 h-3.5" /> No pushes yet.</p>}
    </Card>
  );
};
